package ai.jessie.web.pages.organisations

import ai.jessie.shared.work.WorkCalendarView
import ai.jessie.web.onboarding.onboardingProblem
import ai.jessie.web.onboarding.organisationRequiredProblem
import ai.jessie.web.onboarding.validOrganisationId
import ai.jessie.web.theme.AlertStyle
import ai.jessie.web.theme.ButtonVariant
import ai.jessie.web.theme.Kpi
import ai.jessie.web.theme.appFrame
import ai.jessie.web.theme.homeSideItems
import ai.jessie.web.theme.linkButton
import ai.jessie.web.theme.themedButton
import ai.jessie.web.work.LocalWorkCalendarPort
import ai.jessie.web.work.LocalWorkPort
import ai.jessie.web.work.WorkCalendarPorts
import ai.jessie.web.work.WorkCalendarRoute
import ai.jessie.web.work.WorkCalendarState
import ai.jessie.web.work.calendarDateLabel
import ai.jessie.web.work.calendarHours
import ai.jessie.web.work.calendarMonthName
import ai.jessie.web.work.calendarView
import ai.jessie.web.work.calendarWeekday
import ai.jessie.web.work.validCalendarDate
import ai.jessie.web.work.workCalendar
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.key
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import com.varabyte.kobweb.core.AppGlobals
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.PageContext
import com.varabyte.kobweb.core.isExporting
import com.varabyte.kobweb.silk.style.toAttrs
import org.jetbrains.compose.web.dom.Div

// UC-009. Tenant state is keyed by the routed organisation and range, so another organisation or
// range discards it together with any unsaved work entry. An unusable `view` or `date` query
// parameter is left out, which opens the server's current week.
@Page("{organisationId}/calendar")
@Composable
fun organisationCalendarPage(ctx: PageContext) {
    val organisationId = ctx.route.dynamicParams["organisationId"].orEmpty()
    if (!validOrganisationId(organisationId)) {
        appFrame(
            organisationId,
            "/organisations/$organisationId/calendar",
            "Kalender",
            side = homeSideItems(organisationId),
        ) {
            Div(attrs = AlertStyle.toAttrs()) {
                onboardingProblem(organisationRequiredProblem(), busy = false)
            }
        }
    } else {
        val route =
            WorkCalendarRoute(
                organisationId,
                calendarView(ctx.route.params["view"]),
                ctx.route.params["date"]?.takeIf(::validCalendarDate),
            )
        key(route) { calendarContent(route) }
    }
}

@Composable
private fun calendarContent(route: WorkCalendarRoute) {
    val calendarPort = LocalWorkCalendarPort.current
    val workPort = LocalWorkPort.current
    val scope = rememberCoroutineScope()
    val model =
        remember(calendarPort, workPort, scope, route) {
            WorkCalendarState(WorkCalendarPorts(calendarPort, workPort), scope, route)
        }
    LaunchedEffect(model) { if (!AppGlobals.isExporting) model.load() }
    val editor = model.editor
    // Step 11, A2, A7: a stored or cancelled entry returns to the reloaded calendar.
    LaunchedEffect(editor?.state) { editor?.let { model.settle(it.state) } }
    val authenticationRequired =
        (editor?.state?.error ?: model.state.error)?.authenticationRequired == true
    val calendar =
        model.state.calendar.takeIf { editor?.state?.error == null || editor.state.draft != null }
    appFrame(
        route.organisationId,
        "/organisations/${route.organisationId}/calendar",
        calendar?.let { calendarDateLabel(it.currentDate) } ?: "Kalender",
        kicker = calendar?.let { calendarWeekday(it.currentDate) },
        kpis =
            calendar
                ?.let {
                    listOf(
                        Kpi(
                            if (it.view == WorkCalendarView.WEEK) "Diese Woche"
                            else calendarMonthName(it.rangeStart),
                            calendarHours(it.totalMinutes),
                            "h",
                        )
                    )
                }
                .orEmpty(),
        authenticationRequired = authenticationRequired,
        side = homeSideItems(route.organisationId),
        actions = {
            calendar?.let {
                if (it.days.any { day -> day.date == it.currentDate })
                    themedButton(
                        "Neue Arbeit",
                        { model.actions.createOn(it.currentDate) },
                        variant = ButtonVariant.TINT,
                        disabled = model.state.busy,
                        elementId = "calendar-new-work",
                    )
                else
                    linkButton(
                        "Neue Arbeit",
                        "/organisations/${route.organisationId}/calendar?view=${it.view.name}",
                        variant = ButtonVariant.TINT,
                    )
            }
        },
    ) {
        workCalendar(route, model.state, model.actions, editor)
    }
}
